import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import { GlassButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { LearnFlashcardShell } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { useLearnFlashcardStageTopInsets } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import { ChromeReadyMarker } from "@-/dev.improve.haniverse.components.ui.chrome-ready-marker";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { publishLearnSummaryFlashcardFrame } from "#publish-learn-summary-card-frame";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useCallback, useContext, useRef } from "react";

/**
 * First paint uses only bundled components and synchronous device settings. Keep this separate
 * from the ready view: that view constructs tile queries and destination navigators, and cannot
 * be the last fallback protecting startup from their data dependencies.
 */
export function LearnSummaryPageLoadingFallback({
  onPressStartReviewing,
}: {
  /**
   * Makes the CTA live while the summary cannot load (its study data is unavailable): the frame
   * stays the familiar one, and the tap is where the user learns what is wrong. Omitted, the CTA is
   * the disabled loading placeholder ("Loading Reviews..." with a spinner). The card stays an
   * empty shell — loading is indicated only on this button.
   */
  onPressStartReviewing?: () => void;
} = {}) {
  const bottomInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const { cardTop } = useLearnFlashcardStageTopInsets();
  const isLoadingReviews = onPressStartReviewing === undefined;
  const ctaLabel = isLoadingReviews ? "Loading Reviews..." : "Start Reviewing";
  // Signing in from the welcome galaxy lands on this frame first: the galaxy squares off onto it.
  const frameRef = useRef<View>(null);
  const publishFrame = useCallback(() => publishLearnSummaryFlashcardFrame(frameRef.current), []);
  return (
    <View className="min-h-0 flex-1 bg-muted" testID="learn-summary-local-welcome">
      <ScreenReadyMarker screen="learn-summary" />
      {/* The tab bar defers the startup handoff to Learn on `/learn`, and `LearnSummarySurface` only
          raises it while focused. This fallback is also what the Learn layout's data gate and
          router fallback render at `/learn` with no summary mounted, so it raises the handoff too:
          otherwise those states held the splash until the deadline in `lib/bootsplash`. */}
      <ChromeReadyMarker />
      <View
        className="mx-2.25 mb-2.25 min-h-0 flex-1"
        onLayout={publishFrame}
        ref={frameRef}
        style={{
          marginLeft: isLandscape ? diameter + 18 : undefined,
          marginRight: isLandscape ? diameter + 18 : undefined,
          marginTop: cardTop,
        }}
      >
        <View
          accessibilityElementsHidden
          className="absolute inset-0"
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
        >
          <LearnFlashcardShell
            className="absolute inset-0"
            testID="learn-summary-successor-placeholder"
          >
            {null}
          </LearnFlashcardShell>
        </View>
        <LearnFlashcardShell className="min-h-0 flex-1 shadow-sm" testID="learn-summary-card">
          {null}
        </LearnFlashcardShell>
      </View>
      <View
        className={
          isLandscape
            ? "absolute right-2.25 bottom-2.25 items-center gap-2.25"
            : "mx-2.25 items-center"
        }
        style={
          isLandscape
            ? { top: diameter + 18, width: diameter }
            : { height: diameter, marginBottom: 9 + bottomInset }
        }
      >
        <GlassButton
          accessibilityLabel={ctaLabel}
          accessibilityState={isLoadingReviews ? { busy: true, disabled: true } : undefined}
          className={
            isLandscape
              ? "min-h-0 w-full flex-1 rounded-full p-0 shadow-xs"
              : "h-full rounded-full px-6 py-0 shadow-xs"
          }
          disabled={isLoadingReviews}
          isLoading={isLoadingReviews}
          onPress={onPressStartReviewing}
          size={isLandscape ? "fill" : undefined}
        >
          {isLoadingReviews ? <ActivityIndicator size="small" /> : null}
          {isLandscape ? (
            <Icon className="text-foreground" name="LuPlay" size={22} />
          ) : (
            <Text
              adjustsFontSizeToFit
              className="text-base leading-6"
              colorVariant="default"
              numberOfLines={1}
            >
              {ctaLabel}
            </Text>
          )}
        </GlassButton>
      </View>
    </View>
  );
}
